import { AppBar, Grid, Toolbar } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import * as kapi from "api/Knowledge";
import { FeedStyleClasses, FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonBack from "components/Buttons/ButtonBack";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonLink from "components/Buttons/ButtonLink";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconAdd from "components/Icons/IconAdd";
import IconAddPeople from "components/Icons/IconAddPeople";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { PublicationType } from "models/ApiEnums";
import { PublicationAdminCourseDto, SavePublicationResult } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import CourseAdminCard from "views/Publication/Components/CourseAdminCard";
import CreatePost from "views/Publication/CreatePost";
import CourseEnrollDrawer from "views/Publication/Dialogs/CourseEnrollDrawer";
import PublicationSelectionDrawer, { PublicationSelectionResult } from "views/Publication/Dialogs/PublicationSelectionDrawer";
import FeedCourseItems, { FeedCourseItemsComponent } from "views/Timeline/Components/FeedCourseItems";
import { publicationFactory } from "views/Timeline/Components/FeedHelpers";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    publication: PublicationAdminCourseDto;
    formCycle: RequestLifeCycle;
    lastError: any;
    reloadOnExit: boolean;
    mustBePublished: boolean;
    changed: boolean;
}

export interface IProps {
    courseId: string;
    onClose: (reload: boolean) => void;
}

type Props = IProps & WithSnackbar & WithTranslation & WithStyles<typeof FormStyleClasses> & WithStyles<typeof FeedStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////

class CourseAdminCardEdit extends React.Component<Props, IState> {

    private feedRef: FeedCourseItemsComponent;

    constructor(props: Props) {
        super(props);

        this.state = {
            publication: null,
            reloadOnExit: false,
            dialog: null,
            formCycle: RequestLifeCycle.Loading,
            lastError: null,
            mustBePublished: false,
            changed: false
        };
    }

    ///
    private loadCourse() {
        api.getCoursePublicationAdmin(this.props.courseId)
            .then((result) => {
                this.setState({
                    publication: result,
                    formCycle: RequestLifeCycle.None,
                    mustBePublished: result.mustBePublished,
                    changed: false,
                    // Força recarregar ao voltar (novo curso)
                    reloadOnExit: result.numberOfItems == 0
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private reloadCourse() {
        this.setState({ dialog: null });

        api.getCoursePublicationAdmin(this.props.courseId)
            .then((result) => {
                this.setState({ publication: result, reloadOnExit: true });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private addPublicationItem(publicationId: string) {
        kapi.courseAddPublication(this.props.courseId, publicationId)
            .then((result) => {
                if (result.saved) {
                    this.setState({ mustBePublished: result.mustBePublished, changed: result.changed }, () => {
                        this.feedRef.feedReset();
                        this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.CourseAdminEdit.AddItemSuccess"), "success");
                    });
                }
                else {
                    this.setState({ dialog: null }, () => {
                        this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                    });
                }
            })
            .catch((error) => {
                this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleEdit = () => {
        this.setState({
            dialog: publicationFactory(
                this.state.publication.publicationId,
                PublicationType.Course,
                (result) => {
                    if (result) {
                        this.reloadCourse();
                    }
                    else {
                        this.setState({ dialog: null });
                    }
                },
                { buttonCloseText: this.props.t("Common.Buttons.ConcludeText") }
            )
        });
    };

    ///
    private handleDelete = (publicationId: string, enrollments: number) => {
        this.setState({
            dialog: <MuiConfirmation
                title={this.props.t("Views.Publication.Components.CourseAdminEdit.ConfirmationDeleteTitle")}
                message={
                    enrollments > 0
                        ? this.props.t("Views.Publication.CourseAdminList.ConfirmationDeleteWithEnrollMessage")
                        : this.props.t("Views.Publication.Components.CourseAdminEdit.ConfirmationDeleteMessage")
                }
                onClose={
                    (result) => {
                        if (result) {

                            this.setState({ formCycle: RequestLifeCycle.Posting });

                            kapi.deletePublication(publicationId, PublicationType.Course)
                                .then((result) => {
                                    if (result) {
                                        this.setState({ dialog: null, reloadOnExit: true, formCycle: RequestLifeCycle.None });
                                        this.props.onClose(true);
                                    }
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                });
                        }
                        else {
                            this.setState({ dialog: null });
                        }
                    }
                }
            />
        });
    };

    ///
    private handleExpand = (item: PublicationAdminCourseDto, textExpanded: boolean) => {
        const d = { ...item };

        if (textExpanded && d.body.fullContent == null) {

            api.getPublicationAdminBody(item.publicationId)
                .then((body) => {
                    this.setState(state => {
                        state.publication.body = body;
                        return state;
                    });
                });
        }
        else {
            this.setState(state => {
                state.publication.body.fullContentLoaded = textExpanded;
                return state;
            });
        }
    };

    ///
    private handleCreatePublication = (result: SavePublicationResult) => {
        if (result) this.addPublicationItem(result.publicationId);
        this.setState({ dialog: null });
    };

    ///
    private handlePublicationSelection = (result: PublicationSelectionResult): boolean => {
        if (result != null) {
            if (result.publication != null) {
                this.addPublicationItem(result.publication.publicationId);
            }
            else if (result.createContent) {

                switch (result.createContent) {
                    case PublicationType.Post:
                        this.setState({
                            dialog: (
                                <CreatePost
                                    parentId={this.props.courseId}
                                    onClose={this.handleCreatePublication}
                                />
                            )
                        });
                        break;

                    default:
                        return false;
                }
            }
        }
        return true;
    };

    ///
    private handleAddItem = () => {
        this.setState({
            dialog: (
                <PublicationSelectionDrawer
                    title={this.props.t("Common.Literals.PublicationSelection")}
                    excludeOptions={
                        {
                            excludePublicationsType: [PublicationType.List, PublicationType.Poll, PublicationType.Survey, PublicationType.Course, PublicationType.Question],
                            excludePublicationsFromCourseId: this.props.courseId
                        }
                    }
                    createPublications={[PublicationType.Post]}
                    onSelect={this.handlePublicationSelection}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handlePublish = () => {

        this.setState({ formCycle: RequestLifeCycle.Posting });

        kapi.coursePublish(this.props.courseId)
            .then((result) => {
                if (result) {
                    this.setState({ mustBePublished: false, formCycle: RequestLifeCycle.None, reloadOnExit: true }, () => {
                        this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.CourseAdminEdit.PublishedSuccess"), "success");

                        if (this.state.publication.statistics.enrollments === 0) {
                            this.setState({
                                dialog: <MuiConfirmation
                                    title={this.props.t("Views.Publication.Components.CourseAdminEdit.ConfirmationEnrollTitle")}
                                    message={this.props.t("Views.Publication.Components.CourseAdminEdit.ConfirmationEnrollMessage")}
                                    icon={<IconAddPeople size="2x" />}
                                    onClose={
                                        (confirm) => {
                                            if (confirm) this.handleEnroll(this.state.publication);
                                            else this.setState({ dialog: null }, () => this.props.onClose(true));
                                        }
                                    }
                                />

                            });
                        }
                        else {
                            this.props.onClose(true);
                        }
                    });
                }
                else {
                    this.setState({ dialog: null, formCycle: RequestLifeCycle.None }, () => {
                        this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                    });
                }

            })
            .catch((error) => this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed }));
    };

    ///
    private handleRollback = () => {

        this.setState({ formCycle: RequestLifeCycle.Posting });

        kapi.courseRollback(this.props.courseId)
            .then((result) => {
                if (result) {
                    this.setState({ mustBePublished: false, formCycle: RequestLifeCycle.None, reloadOnExit: true }, () => {
                        this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.CourseAdminEdit.RollbackSuccess"), "success");
                        this.props.onClose(this.state.reloadOnExit);
                    });
                }
                else {
                    this.setState({ dialog: null, formCycle: RequestLifeCycle.None }, () => {
                        this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                    });
                }

            })
            .catch((error) => this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed }));
    };

    ///
    private handleEnroll = (item: PublicationAdminCourseDto) => {
        this.setState({
            dialog: (
                <CourseEnrollDrawer
                    course={item}
                    onClose={
                        (count) => {
                            this.setState(
                                { dialog: null },
                                () => this.props.onClose(count != null && count > 0)
                            );
                        }
                    }
                />
            )
        });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>

                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            {
                                this.state.changed && this.state.publication.publishDate != null
                                    ? (
                                        <ButtonCancel
                                            onClick={
                                                () => {
                                                    this.setState({
                                                        dialog: <MuiConfirmation
                                                            message={this.props.t("Views.Publication.Components.CourseAdminEdit.RollbackMessage")}
                                                            onClose={
                                                                (confirm) => {
                                                                    if (confirm) this.setState({ dialog: null }, () => this.handleRollback());
                                                                    else this.setState({ dialog: null });
                                                                }
                                                            }
                                                        />
                                                    });
                                                }
                                            }
                                        />
                                    )
                                    : <ButtonBack onClick={() => this.props.onClose(this.state.reloadOnExit)} />
                            }
                        </Grid>
                        <Grid item>
                            <MuiColor variant="warning">
                                <ButtonLink
                                    text={this.props.t("Views.Publication.Components.CourseAdminEdit.ButtonAddItem")}
                                    startAddorment={<IconAdd />}
                                    onClick={this.handleAddItem}
                                />
                            </MuiColor>
                        </Grid>
                        <Grid item>
                            <MuiColor variant="success">
                                <ButtonLink
                                    text={this.props.t("Views.Publication.Components.CourseAdminEdit.ButtonPublish")}
                                    disabled={!this.state.mustBePublished}
                                    onClick={this.handlePublish}
                                />
                            </MuiColor>
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar >
        );
    }

    ///
    public componentDidMount() {
        this.loadCourse();
    }

    ///
    public render() {

        const { dialog, publication, formCycle, lastError } = this.state;

        if (formCycle == RequestLifeCycle.Loading)
            return <RequestIndicator type="waiting" />;

        if (formCycle == RequestLifeCycle.LoadFailed) {
            return <RequestErrorReport error={lastError} />;
        }

        return (
            <>
                {formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {dialog}

                {this.renderToolbar()}

                <MuiContainer margin="dense">
                    <CourseAdminCard
                        item={publication}
                        fullView={true}
                        allowManageEnroll={false}
                        onEdit={this.handleEdit}
                        onDelete={this.handleDelete}
                        onExpand={this.handleExpand}
                        onNeedReload={() => this.reloadCourse()}
                        onUpdateTopics={
                            (topics) => {
                                this.setState(state => {
                                    state.publication.topics = topics;
                                    return { ...state, reloadOnExit: true };
                                });
                            }
                        }
                    />
                </MuiContainer>

                <FeedCourseItems
                    classes={this.props.classes}
                    courseId={publication.publicationId}
                    onGetReference={(ref) => this.feedRef = ref}
                    onItemChanged={(mustBePublished, changed) => this.setState({ mustBePublished, changed, reloadOnExit: true })}
                />
            </>
        );
    }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...FormStyleClasses(theme),
        ...FeedStyleClasses(theme)
    }),
    { withTheme: true },
);

export default withTranslation()(withAllStyles(withSnackbar(CourseAdminCardEdit)));